import { useRef } from "react"

import { PostAuthPasswordRequestBody as FormSchema } from "@boundless/bl-api-client/allauth/allauth.zod"
import { usePostAuthPasswordRequest } from "@boundless/bl-api-client/allauth/auth"
import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useWatch } from "react-hook-form"
import { z } from "zod"

import { FormInput } from "@/components/form-elements/FormInput"
import {
  FormTurnstile,
  type FormTurnstileHandle,
} from "@/components/form-elements/FormTurnstile"
import { Button, LoadingButton } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { getAuthClient, type ErrorType } from "@/lib/clients"
import { applyErrorsToForm } from "@/lib/forms/utils"

import type { ErrorResponse } from "@boundless/bl-api-client/allauth/models"

export function ForgotPasswordForm({
  onSuccess,
  ...props
}: React.ComponentPropsWithoutRef<"form"> & {
  onSuccess?: () => void
}) {
  const client = getAuthClient()
  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()
  const turnstileRef = useRef<FormTurnstileHandle>(null)
  const turnstileEnabled =
    boundlessConfig.pwResetCaptchaEnabled &&
    Boolean(boundlessConfig.turnstileSiteKey)

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
      captcha: "",
    },
  })
  const captchaToken = useWatch({ control: form.control, name: "captcha" })

  const { isPending, mutate: requestPasswordReset } =
    usePostAuthPasswordRequest({
      mutation: {
        onSuccess: () => {
          form.reset()
          onSuccess?.()
        },
        onError: (error: ErrorType<ErrorResponse>) => {
          turnstileRef.current?.reset()
          applyErrorsToForm(form.setError, error)
        },
      },
    })

  function handleSubmit(data: z.infer<typeof FormSchema>) {
    requestPasswordReset({ client, data })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} {...props}>
        <div className="flex flex-col space-y-4">
          <FormInput
            name="email"
            placeholder="Email address"
            type="email"
            autoComplete="email"
            className="w-full"
            autoFocus
            required
            disabled={isPending}
          />

          {turnstileEnabled && (
            <FormTurnstile
              ref={turnstileRef}
              action="password_reset"
              className="mx-auto"
              name="captcha"
              siteKey={boundlessConfig.turnstileSiteKey}
            />
          )}

          {form.formState.errors.root && (
            <p className="text-destructive text-sm font-medium">
              {form.formState.errors.root.message}
            </p>
          )}

          {isPending ? (
            <LoadingButton>Reset Password</LoadingButton>
          ) : (
            <Button type="submit" disabled={turnstileEnabled && !captchaToken}>
              Reset Password
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
